var _a;
import * as THREE from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import Application from '../Application';
import EventEmitter from '../Utils/EventEmitter';
import { SCREEN_CONFIG } from './deskSceneConfig';
import UIEventBus from '../UI/EventBus';
const SCREEN_SIZE = SCREEN_CONFIG.size;
const IFRAME_PADDING = (_a = SCREEN_CONFIG.iframePadding) !== null && _a !== void 0 ? _a : 0;
const IFRAME_SIZE = {
    w: SCREEN_SIZE.w - 2 * IFRAME_PADDING,
    h: SCREEN_SIZE.h - 2 * IFRAME_PADDING,
};
export default class MonitorScreen extends EventEmitter {
    constructor() {
        super();
        this.screenIframe = null;
        this.innerLoadTimeoutId = null;
        this.innerLoadTimeoutMs = 12000;
        this.lastMusicProgressPostMs = 0;
        this.setIframeUrl = () => {
            document.removeEventListener('loadingScreenDone', this.setIframeUrl);
            if (!this.screenIframe)
                return;
            const urlParams = new URLSearchParams(window.location.search);
            let url;
            if (urlParams.has('dev') && SCREEN_CONFIG.iframeUrlDev) {
                url = SCREEN_CONFIG.iframeUrlDev;
            }
            else {
                const host = typeof window !== 'undefined' ? window.location.hostname : '';
                if (host === 'filipsj.com' || host === 'www.filipsj.com') {
                    url = 'https://os.filipsj.com/';
                }
                else {
                    url = SCREEN_CONFIG.iframeUrl;
                }
            }
            this.screenIframe.src = url;
            this.startInnerLoadTimeout(url);
            window.addEventListener('message', this.onInnerMessage);
        };
        this.onInnerMessage = (event) => {
            var _a, _b;
            if (((_a = event.data) === null || _a === void 0 ? void 0 : _a.type) === 'inner-loaded') {
                this.clearInnerLoadTimeout();
            }
            if (((_b = event.data) === null || _b === void 0 ? void 0 : _b.type) === 'mute-background-music' && typeof event.data.mute === 'boolean') {
                UIEventBus.dispatch('setBackgroundMusicMuted', event.data.mute);
            }
        };
        this.application = new Application();
        this.scene = this.application.scene;
        this.cssScene = this.application.cssScene;
        this.sizes = this.application.sizes;
        this.resources = this.application.resources;
        this.screenSize = new THREE.Vector2(SCREEN_SIZE.w, SCREEN_SIZE.h);
        this.camera = this.application.camera;
        const [px, py, pz] = SCREEN_CONFIG.position;
        const [rx, ry, rz] = SCREEN_CONFIG.rotation;
        this.position = new THREE.Vector3(px, py, pz);
        this.rotation = new THREE.Euler(rx * THREE.MathUtils.DEG2RAD, ry * THREE.MathUtils.DEG2RAD, rz * THREE.MathUtils.DEG2RAD);
        this.videoTextures = {};
        this.mouseClickInProgress = false;
        this.shouldLeaveMonitor = false;
        // Create screen
        this.initializeScreenEvents();
        this.createIframe();
        const maxOffset = this.createTextureLayers();
        if (SCREEN_CONFIG.showBezel) {
            this.createEnclosingPlanes(maxOffset);
        }
        this.createPerspectiveDimmer(maxOffset);
    }
    initializeScreenEvents() {
        document.addEventListener('mousemove', (event) => {
            // @ts-ignore
            const id = event.target.id;
            if (id === 'computer-screen') {
                // @ts-ignore
                event.inComputer = true;
            }
            // @ts-ignore
            this.inComputer = event.inComputer;
            if (this.inComputer && !this.prevInComputer) {
                this.camera.trigger('enterMonitor');
            }
            if (!this.inComputer &&
                this.prevInComputer &&
                !this.mouseClickInProgress) {
                this.camera.trigger('leftMonitor');
            }
            if (!this.inComputer &&
                this.mouseClickInProgress &&
                this.prevInComputer) {
                this.shouldLeaveMonitor = true;
            }
            else {
                this.shouldLeaveMonitor = false;
            }
            this.application.mouse.trigger('mousemove', [event]);
            this.prevInComputer = this.inComputer;
        }, false);
        document.addEventListener('mousedown', (event) => {
            // @ts-ignore
            this.inComputer = event.inComputer;
            this.application.mouse.trigger('mousedown', [event]);
            this.mouseClickInProgress = true;
            this.prevInComputer = this.inComputer;
        }, false);
        document.addEventListener('mouseup', (event) => {
            // @ts-ignore
            this.inComputer = event.inComputer;
            this.application.mouse.trigger('mouseup', [event]);
            if (this.shouldLeaveMonitor) {
                this.camera.trigger('leftMonitor');
                this.shouldLeaveMonitor = false;
            }
            this.mouseClickInProgress = false;
            this.prevInComputer = this.inComputer;
        }, false);
    }
    /**
     * Creates the iframe for the computer screen
     */
    createIframe() {
        var _a, _b, _c;
        // Create container
        const container = document.createElement('div');
        container.style.width = this.screenSize.width + 'px';
        container.style.height = this.screenSize.height + 'px';
        container.style.position = 'relative';
        container.style.opacity = '1';
        container.style.overflow = 'hidden';
        const top = (_a = SCREEN_CONFIG.borderRadiusTop) !== null && _a !== void 0 ? _a : 0;
        const bottom = (_b = SCREEN_CONFIG.borderRadiusBottom) !== null && _b !== void 0 ? _b : 0;
        const bgColor = (_c = SCREEN_CONFIG.containerBackground) !== null && _c !== void 0 ? _c : '#e8e8e8';
        // Apply same border-radius to the container so the *entire* painted area is rounded (avoids sharp corners showing through)
        if (top > 0 || bottom > 0) {
            container.style.borderRadius = `${top}px ${top}px ${bottom}px ${bottom}px`;
        }
        // Background on the container itself so rounded corners are this color (not transparent → black)
        container.style.background = bgColor;
        container.style.border = 'none';
        container.style.outline = 'none';
        // Force compositing layer so background + border-radius paint correctly with 3D transform
        container.style.transform = 'translateZ(0)';
        container.style.willChange = 'transform';
        // Inset box-shadow only when rounded corners (avoid 1px bezel when radius is 0)
        if (top > 0 || bottom > 0) {
            container.style.boxShadow = `inset 0 0 0 1px ${bgColor}`;
        }
        // Solid background layer with same radius so the clipped iframe area shows this color
        const bgLayer = document.createElement('div');
        bgLayer.style.position = 'absolute';
        bgLayer.style.inset = '0';
        bgLayer.style.background = bgColor;
        bgLayer.style.borderRadius = `${top}px ${top}px ${bottom}px ${bottom}px`;
        bgLayer.style.pointerEvents = 'none';
        container.appendChild(bgLayer);
        // Create iframe
        const iframe = document.createElement('iframe');
        // Bubble mouse move events to the main application, so we can affect the camera
        iframe.onload = () => {
            if (iframe.contentWindow) {
                window.addEventListener('message', (event) => {
                    var evt = new CustomEvent(event.data.type, {
                        bubbles: true,
                        cancelable: false,
                    });
                    // @ts-ignore
                    evt.inComputer = true;
                    if (event.data.type === 'mousemove') {
                        var clRect = iframe.getBoundingClientRect();
                        const { top, left, width, height } = clRect;
                        const widthRatio = width / IFRAME_SIZE.w;
                        const heightRatio = height / IFRAME_SIZE.h;
                        // @ts-ignore
                        evt.clientX = Math.round(event.data.clientX * widthRatio + left);
                        //@ts-ignore
                        evt.clientY = Math.round(event.data.clientY * heightRatio + top);
                    }
                    else if (event.data.type === 'keydown') {
                        // @ts-ignore
                        evt.key = event.data.key;
                    }
                    else if (event.data.type === 'keyup') {
                        // @ts-ignore
                        evt.key = event.data.key;
                    }
                    iframe.dispatchEvent(evt);
                });
            }
        };
        // Don't load inner site until loading animation is done (keeps inner screen hidden during load)
        iframe.src = 'about:blank';
        this.screenIframe = iframe;
        document.addEventListener('loadingScreenDone', this.setIframeUrl);
        iframe.style.position = 'relative';
        iframe.style.zIndex = '1';
        // Slightly oversize iframe to eliminate 1px bezel from sub-pixel rounding (container has overflow: hidden)
        const iframeOversize = 2;
        iframe.style.width = this.screenSize.width + iframeOversize + 'px';
        iframe.style.height = this.screenSize.height + iframeOversize + 'px';
        iframe.style.marginLeft = '-' + iframeOversize / 2 + 'px';
        iframe.style.marginTop = '-' + iframeOversize / 2 + 'px';
        iframe.style.padding = IFRAME_PADDING + 'px';
        iframe.style.boxSizing = 'border-box';
        iframe.style.opacity = '1';
        const iframeTop = top > 0 ? Math.max(0, top - 2) : 0;
        const iframeBottom = bottom > 0 ? Math.max(0, bottom - 2) : 0;
        iframe.style.borderRadius = `${iframeTop}px ${iframeTop}px ${iframeBottom}px ${iframeBottom}px`;
        iframe.style.border = 'none';
        iframe.style.outline = 'none';
        iframe.style.display = 'block';
        iframe.className = 'computer-screen-frame';
        iframe.id = 'computer-screen';
        iframe.frameBorder = '0';
        iframe.setAttribute('frameborder', '0');
        iframe.title = 'Portfolio OS';
        // Add iframe to container
        container.appendChild(iframe);
        // Create CSS plane
        this.createCssPlane(container);
    }
    startInnerLoadTimeout(fallbackUrl) {
        this.clearInnerLoadTimeout();
        this.innerLoadTimeoutId = setTimeout(() => {
            this.innerLoadTimeoutId = null;
            this.resetScreenAfterBlocked(fallbackUrl);
        }, this.innerLoadTimeoutMs);
    }
    clearInnerLoadTimeout() {
        if (this.innerLoadTimeoutId) {
            clearTimeout(this.innerLoadTimeoutId);
            this.innerLoadTimeoutId = null;
        }
    }
    /** Reset iframe after blocked/failed load (e.g. ERR_BLOCKED_BY_RESPONSE) so the screen doesn't stay broken. */
    resetScreenAfterBlocked(fallbackUrl) {
        if (!this.screenIframe)
            return;
        window.removeEventListener('message', this.onInnerMessage);
        this.screenIframe.src = 'about:blank';
        setTimeout(() => {
            if (this.screenIframe) {
                this.screenIframe.src = fallbackUrl;
                window.addEventListener('message', this.onInnerMessage);
                this.startInnerLoadTimeout(fallbackUrl);
            }
        }, 300);
    }
    /**
     * Creates a CSS plane and GL plane to properly occlude the CSS plane
     * @param element the element to create the css plane for
     */
    createCssPlane(element) {
        // Create CSS3D object
        const object = new CSS3DObject(element);
        // copy monitor position and rotation
        object.position.copy(this.position);
        object.rotation.copy(this.rotation);
        // Add to CSS scene
        this.cssScene.add(object);
        // Create GL plane
        const material = new THREE.MeshLambertMaterial();
        material.side = THREE.DoubleSide;
        material.opacity = 0;
        material.transparent = true;
        // NoBlending allows the GL plane to occlude the CSS plane
        material.blending = THREE.NoBlending;
        // Create plane geometry
        const geometry = new THREE.PlaneGeometry(this.screenSize.width, this.screenSize.height);
        // Create the GL plane mesh
        const mesh = new THREE.Mesh(geometry, material);
        // Copy the position, rotation and scale of the CSS plane to the GL plane
        mesh.position.copy(object.position);
        mesh.rotation.copy(object.rotation);
        mesh.scale.copy(object.scale);
        // Add to gl scene
        this.scene.add(mesh);
    }
    /**
     * Creates the texture layers for the computer screen (smudge, shadow, video overlay).
     * If showOverlayLayers is false in config, skips layers and returns offset for dimmer only.
     * @returns the maximum offset of the texture layers
     */
    createTextureLayers() {
        const scaleFactor = 4;
        const defaultMaxOffset = 24 * scaleFactor;
        if (!SCREEN_CONFIG.showOverlayLayers) {
            return defaultMaxOffset;
        }
        const textures = this.resources.items.texture;
        this.getVideoTextures('video-1');
        this.getVideoTextures('video-2');
        // Construct the texture layers
        const layers = {
            smudge: {
                texture: textures.monitorSmudgeTexture,
                blending: THREE.AdditiveBlending,
                opacity: 0.12,
                offset: 24,
            },
            innerShadow: {
                texture: textures.monitorShadowTexture,
                blending: THREE.NormalBlending,
                opacity: 1,
                offset: 5,
            },
            video: {
                texture: this.videoTextures['video-1'],
                blending: THREE.AdditiveBlending,
                opacity: 0.5,
                offset: 10,
            },
            video2: {
                texture: this.videoTextures['video-2'],
                blending: THREE.AdditiveBlending,
                opacity: 0.1,
                offset: 15,
            },
        };
        // Declare max offset
        let maxOffset = -1;
        // Add the texture layers to the screen
        for (const [_, layer] of Object.entries(layers)) {
            const offset = layer.offset * scaleFactor;
            this.addTextureLayer(layer.texture, layer.blending, layer.opacity, offset);
            // Calculate the max offset
            if (offset > maxOffset)
                maxOffset = offset;
        }
        // Return the max offset
        return maxOffset;
    }
    getVideoTextures(videoId) {
        const video = document.getElementById(videoId);
        if (!video) {
            setTimeout(() => {
                this.getVideoTextures(videoId);
            }, 100);
        }
        else {
            this.videoTextures[videoId] = new THREE.VideoTexture(video);
        }
    }
    /**
     * Adds a texture layer to the screen
     * @param texture the texture to add
     * @param blending the blending mode
     * @param opacity the opacity of the texture
     * @param offset the offset of the texture, higher values are further from the screen
     */
    addTextureLayer(texture, blendingMode, opacity, offset) {
        // Create material
        const material = new THREE.MeshBasicMaterial({
            map: texture,
            blending: blendingMode,
            side: THREE.DoubleSide,
            opacity,
            transparent: true,
        });
        // Create geometry
        const geometry = new THREE.PlaneGeometry(this.screenSize.width, this.screenSize.height);
        // Create mesh
        const mesh = new THREE.Mesh(geometry, material);
        // Copy position and apply the depth offset
        mesh.position.copy(this.offsetPosition(this.position, new THREE.Vector3(0, 0, offset)));
        // Copy rotation
        mesh.rotation.copy(this.rotation);
        this.scene.add(mesh);
    }
    /**
     * Creates enclosing planes for the computer screen
     * @param maxOffset the maximum offset of the texture layers
     */
    createEnclosingPlanes(maxOffset) {
        // Create planes, lots of boiler plate code here because I'm lazy
        const planes = {
            left: {
                size: new THREE.Vector2(maxOffset, this.screenSize.height),
                position: this.offsetPosition(this.position, new THREE.Vector3(-this.screenSize.width / 2, 0, maxOffset / 2)),
                rotation: new THREE.Euler(0, 90 * THREE.MathUtils.DEG2RAD, 0),
            },
            right: {
                size: new THREE.Vector2(maxOffset, this.screenSize.height),
                position: this.offsetPosition(this.position, new THREE.Vector3(this.screenSize.width / 2, 0, maxOffset / 2)),
                rotation: new THREE.Euler(0, 90 * THREE.MathUtils.DEG2RAD, 0),
            },
            top: {
                size: new THREE.Vector2(this.screenSize.width, maxOffset),
                position: this.offsetPosition(this.position, new THREE.Vector3(0, this.screenSize.height / 2, maxOffset / 2)),
                rotation: new THREE.Euler(90 * THREE.MathUtils.DEG2RAD, 0, 0),
            },
            bottom: {
                size: new THREE.Vector2(this.screenSize.width, maxOffset),
                position: this.offsetPosition(this.position, new THREE.Vector3(0, -this.screenSize.height / 2, maxOffset / 2)),
                rotation: new THREE.Euler(90 * THREE.MathUtils.DEG2RAD, 0, 0),
            },
        };
        // Add each of the planes
        for (const [_, plane] of Object.entries(planes)) {
            this.createEnclosingPlane(plane);
        }
    }
    /**
     * Creates a plane for the enclosing planes
     * @param plane the plane to create
     */
    createEnclosingPlane(plane) {
        var _a;
        const bezelHex = (_a = SCREEN_CONFIG.bezelColor) !== null && _a !== void 0 ? _a : '#e5e5ea';
        const bezelColor = parseInt(bezelHex.replace('#', ''), 16);
        const material = new THREE.MeshBasicMaterial({
            side: THREE.DoubleSide,
            color: bezelColor,
        });
        const geometry = new THREE.PlaneGeometry(plane.size.x, plane.size.y);
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.copy(plane.position);
        mesh.rotation.copy(plane.rotation);
        this.scene.add(mesh);
    }
    createPerspectiveDimmer(maxOffset) {
        const material = new THREE.MeshBasicMaterial({
            side: THREE.DoubleSide,
            color: 0x000000,
            transparent: true,
            blending: THREE.AdditiveBlending,
        });
        const plane = new THREE.PlaneGeometry(this.screenSize.width, this.screenSize.height);
        const mesh = new THREE.Mesh(plane, material);
        mesh.position.copy(this.offsetPosition(this.position, new THREE.Vector3(0, 0, maxOffset - 5)));
        mesh.rotation.copy(this.rotation);
        this.dimmingPlane = mesh;
        this.scene.add(mesh);
    }
    /**
     * Offsets a position vector by another vector
     * @param position the position to offset
     * @param offset the offset to apply
     * @returns the new offset position
     */
    offsetPosition(position, offset) {
        const newPosition = new THREE.Vector3();
        newPosition.copy(position);
        newPosition.add(offset);
        return newPosition;
    }
    update() {
        var _a, _b;
        if (this.dimmingPlane) {
            // Keep screen color normal when zoomed; no whitening/dimming by distance
            // @ts-ignore
            this.dimmingPlane.material.opacity = 0;
        }
        const now = performance.now();
        if (now - this.lastMusicProgressPostMs < 200)
            return;
        const win = (_a = this.screenIframe) === null || _a === void 0 ? void 0 : _a.contentWindow;
        const am = (_b = this.application.world) === null || _b === void 0 ? void 0 : _b.audioManager;
        if (!win || !am)
            return;
        const pb = am.getBackgroundMusicPlayback();
        if (!pb)
            return;
        this.lastMusicProgressPostMs = now;
        win.postMessage({
            type: 'music-progress',
            current: pb.current,
            duration: pb.duration,
        }, '*');
    }
}
